import { useDisclosure } from "@msuite/ui/hooks";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import { responseHandler } from "@msuite/ui/utils";
import type { TransferCarrierListItem } from "@msuite/utils/types";
import { Fragment } from "react";
import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";
import { EditTransferCarrierModal } from "@/modals/stock/edit-transfer-carrier-modal";

export function RowActions({ cell }: CellContext<TransferCarrierListItem, "actions">) {
	/** Hooks */
	const { t } = useTranslation();
	const editModal = useDisclosure();
	const asyncModal = useAsyncModal();

	/** Context */
	const { refetch } = useDataTableContext();

	/** Query */
	const { citadel } = useCitadel();

	/** Computed */
	// The same three-state guard `apps/web/src/components/citadel/can.tsx` documents: the menu is
	// an array rather than a subtree, so the gate sits here, and it is dropped only on a definitive
	// denial — while the rules load the server keeps the last word.
	const stockUpdateIsDenied = !!citadel && citadel.cannot("update", "Stock");
	const stockDeleteIsDenied = !!citadel && citadel.cannot("delete", "Stock");

	/** Functions */
	// Confirmed, and the server refuses it outright while material is still on the trolley: the
	// foreign key cascades, so deleting a loaded one would take its open put-aways with it and
	// strand goods already counted as returned.
	async function handleOnDelete() {
		const isConfirmed = await asyncModal.confirm();
		if (!isConfirmed) return;
		const response = await client.stock["transfer-carriers"][":transferCarrierId"].$delete({
			param: { transferCarrierId: cell.row.original.id },
		});
		await responseHandler(response);
		await refetch?.();
	}

	/** Render */
	return (
		<Fragment>
			<RowActionsWrapper to={`/organization/data/stock/transfer-carrier/${cell.row.original.id}`}>
				{!stockUpdateIsDenied && (
					<Menu
						items={createStandardMenuItems({
							onEdit: editModal.onOpen,
							onDelete: stockDeleteIsDenied ? undefined : handleOnDelete,
						})}
					>
						<Menu.Button />
					</Menu>
				)}
			</RowActionsWrapper>
			{editModal.isOpen && (
				<EditTransferCarrierModal
					isOpen={editModal.isOpen}
					onClose={editModal.onClose}
					onComplete={refetch}
					transferCarrierId={cell.row.original.id}
				/>
			)}
			{asyncModal.isOpen && (
				<ConfirmModal
					message={t("stock.delete_transfer_carrier_confirmation")}
					controller={asyncModal}
				/>
			)}
		</Fragment>
	);
}
